import { useContext, useEffect, useMemo, useRef, useState } from "react";
import ReactCountryFlag from "react-country-flag";
import { Link, useLocation } from "react-router-dom";
import { Button } from "antd";
import {
  CompassOutlined,
  EnvironmentOutlined,
  PictureOutlined,
  ReadOutlined,
  SaveOutlined,
  MoreOutlined,
  UserOutlined,
} from "@ant-design/icons";

import { SidebarSection } from "./SidebarSection";
import {
  userPublicationsPath,
  homePath,
  settingsPath,
  userMemoriesPath,
  userMyEventsPath,
  userMyGuidesPath,
  userVisitPath,
  userSavedPath,
  userConnectionsPath,
  publicationDetailPath,
  MOSTRAR_GUIAS,
} from "../../../../routes/routes";
import { createGuidesText } from "../../../../../shared/utils/translations/guides2Texts";
import { LogoNomada } from "../../../uiElements/LogoNomada";
import NewNavigationsButtons from "../NewNavigationsButtons";
import LanguageSelectNavBar from "../../../layout/BlogSection/LanguageSelectNavBar";
import { NotificationsBadgesBlog } from "../../../notifications/NotificationsBadgesBlog";
import Download from "../../../../pages/download/Download";
import { SidebarItem } from "./SidebarItem";
import AvatarLoader from "./AvatarLoader";
import { LangContext } from "../../../../contexts/Contexts";
import { DEFAULT_PROFILE_IMAGE } from "../../../../../infrastructure/utils/normalizeImageSrc";

const SIDEBAR_ACTIVE_ROUTE_KEY = "vnSidebarActiveRoute";

export const SideBarMenuContent = ({
  uiTexts,
  userData,
  srcCandidate,
  isMobile,
  isNotVerified,
  isVerified,
  onVerifyClick,
  onBusinessPartnerClick,
  onAddPropertyClick,
}) => {
  const { pathname } = useLocation();
  const { lang } = useContext(LangContext);
  const [storedActiveRoute, setStoredActiveRoute] = useState(() =>
    localStorage.getItem(SIDEBAR_ACTIVE_ROUTE_KEY),
  );
  const [loading, setLoading] = useState(true);
  const imgRef = useRef(null);
  const loadTokenRef = useRef(0);

  const showSettingsShortcut = !pathname.startsWith(settingsPath);
  const userType = Number(userData?.idTipoUsuario);
  const avatarTypeClass =
    {
      1: "vn-avatar-wrap--unverified",
      2: "vn-avatar-wrap--verified",
      3: "vn-avatar-wrap--business",
      4: "vn-avatar-wrap--broker",
    }[userType] || "vn-avatar-wrap--unverified";
  const currentLang = (lang || userData?.idioma || "en").toLowerCase();
  const t = (key, fallback) => {
    if (typeof fallback === "object") {
      const value = uiTexts?.[key];
      if (value && currentLang === "es" && value === fallback.en) {
        return fallback.es;
      }
      if (value && currentLang === "en" && value === fallback.es) {
        return fallback.en;
      }
      if (value) return value;
      return fallback[currentLang] || fallback.es || fallback.en || "";
    }
    if (uiTexts?.[key]) return uiTexts[key];
    return fallback;
  };
  const truncate = (text, n) =>
    text && text.length > n ? `${text.slice(0, n)}…` : text || "";

  const roleLabel = (labels) => labels[currentLang] || labels.es;

  const roleBadge = {
    2: {
      label: roleLabel({ es: "Usuario verificado", en: "Verified user" }),
      className: "vn-user-role--verified",
    },
    3: {
      label: roleLabel({ es: "Patrocinador", en: "Sponsor" }),
      className: "vn-user-role--business",
    },
    4: {
      label: roleLabel({ es: "Broker", en: "Broker" }),
      className: "vn-user-role--broker",
    },
  }[userType];

  useEffect(() => {
    const src = srcCandidate;

    // si no hay src, no cargues
    if (!src) {
      setLoading(false);
      return;
    }

    // nuevo intento de carga
    loadTokenRef.current += 1;
    const token = loadTokenRef.current;

    setLoading(true);

    // si ya está completa por cache, apaga
    const raf = requestAnimationFrame(() => {
      if (token !== loadTokenRef.current) return;
      if (imgRef.current?.complete) {
        setLoading(false);
      }
    });

    // timeout de seguridad: si no carga ni falla, no bloquees
    const t = setTimeout(() => {
      if (token !== loadTokenRef.current) return;
      setLoading(false);
    }, 4000);

    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(t);
    };
  }, [srcCandidate]);

  const acciones = useMemo(
    () => [
      {
        icon: <ReadOutlined />,
        label: t("LB0050", { es: "Publicaciones", en: "Posts" }),
        route: userPublicationsPath,
      },
      {
        icon: <PictureOutlined />,
        label: t("LB0051", { es: "Memorias", en: "Memories" }),
        route: userMemoriesPath,
      },
      {
        icon: <ReadOutlined />,
        label: roleLabel({ es: "Mis eventos", en: "My events" }),
        route: userMyEventsPath,
      },
      // Gemela de "Mis eventos" para el modulo de Guias, con la misma brujula
      // que lleva Guias en Explora. Solo en ambientes con
      // REACT_APP_MOSTRAR_GUIAS=true (Pre y Demo): en Produccion el modulo no
      // existe y el menu no debe ofrecerlo. El texto sale del diccionario de
      // Guias, que es donde vive el resto de ese modulo.
      ...(MOSTRAR_GUIAS
        ? [
            {
              icon: <CompassOutlined />,
              label: createGuidesText(currentLang === "en")("myGuidesTab"),
              route: userMyGuidesPath,
            },
          ]
        : []),
      {
        icon: <EnvironmentOutlined />,
        label: t("LB0052", { es: "Visitar", en: "Visit" }),
        route: userVisitPath,
      },
      {
        icon: <SaveOutlined />,
        label: t("LB0053", { es: "Guardados", en: "Saved" }),
        route: userSavedPath,
      },
      {
        icon: <UserOutlined />,
        label: t("LB0068", { es: "Conexiones", en: "Connections" }),
        route: userConnectionsPath,
      },
    ],
    [currentLang, uiTexts],
  );

  const actionSectionTitle = t("LB0921", { es: "Acciones", en: "Actions" });

  const isDetailRoute =
    pathname === publicationDetailPath ||
    pathname.startsWith("/forum/publication");

  const isCurrentRoute = (route) =>
    pathname === route || pathname.startsWith(`${route}/`);

  useEffect(() => {
    const matchedRoute = acciones.find((item) => isCurrentRoute(item.route))?.route;

    if (matchedRoute) {
      localStorage.setItem(SIDEBAR_ACTIVE_ROUTE_KEY, matchedRoute);
      setStoredActiveRoute(matchedRoute);
    }
  }, [acciones, pathname]);

  const rememberActiveRoute = (route) => {
    localStorage.setItem(SIDEBAR_ACTIVE_ROUTE_KEY, route);
    setStoredActiveRoute(route);
  };

  const isActiveRoute = (route) =>
    isCurrentRoute(route) || (isDetailRoute && storedActiveRoute === route);

  const finish = () => {
    // sólo apaga si sigue siendo el intento actual
    const token = loadTokenRef.current;
    // microtask para evitar race con render
    queueMicrotask(() => {
      if (token === loadTokenRef.current) setLoading(false);
    });
  };

  return (
    <>
      <Link
        to={homePath}
        className="vn-brand-sidebar"
        aria-label="Ir al inicio"
      >
        <LogoNomada logoColor={2} />
      </Link>

      <div className="vn-profile">

        <Link
          to={userPublicationsPath}
          className={`vn-avatar-wrap ${avatarTypeClass}`}
        >
          <AvatarLoader loading={loading} />
          <img
            ref={imgRef}
            src={srcCandidate}
            alt="profile"
            className="vn-avatar"
            onLoad={finish}
            onError={(event) => {
              event.currentTarget.onerror = null;
              event.currentTarget.src = DEFAULT_PROFILE_IMAGE;
              finish();
            }}
            loading="eager"
            decoding="async"
          />
        </Link>

        <Link to={userPublicationsPath} className="vn-user-name">
          {userData?.nombres}
        </Link>

        <div className="vn-row">
          <Link to={userPublicationsPath} className="vn-nick">
            {isMobile ? truncate(userData?.nickName, 10) : userData?.nickName}
          </Link>
          {!!userData?.idNacionalidad && (
            <ReactCountryFlag
              className="vn-flag"
              countryCode={userData?.idNacionalidad}
              svg
            />
          )}
        </div>

        {roleBadge ? (
          <div className={`vn-user-role ${roleBadge.className}`}>
            {roleBadge.label}
          </div>
        ) : null}

        {/* <div className="vn-location">
          {isMobile
            ? truncate(userData?.localizacion, 12)
            : userData?.localizacion}
        </div> */}

        <div className="vn-notifications-container">
          {showSettingsShortcut ? (
          <Link
            to={settingsPath}
            className="nb-settings-link"
            aria-label="Más opciones"
          >
            <MoreOutlined className="nb-settings-icon" />
          </Link>
          ) : null}
          <NotificationsBadgesBlog />
          <LanguageSelectNavBar />
        </div>

        <Download />

        {/* <div style={{ marginTop: "12px" }}>
          {isNotVerified ? (
            <Button className="button-side-bar" onClick={onVerifyClick}>
              Get the verification
            </Button>
          ) : isVerified ? (
            <Button
              className="button-side-bar"
              onClick={onBusinessPartnerClick}
            >
              Become a business partner
            </Button>
          ) : (
            <Button className="button-side-bar" onClick={onAddPropertyClick}>
              Add Property
            </Button>
          )}
        </div> */}
      </div>

      <hr className="vn-sep" />

      <div className="vn-scroll">
        <SidebarSection
          title={actionSectionTitle}
          storageKey="vn-sidebar-section-actions-collapsed"
        >
          {acciones.map((item) => (
            <SidebarItem
              key={item.label}
              icon={item.icon}
              label={item.label}
              to={item.route}
              active={isActiveRoute(item.route)}
              onClick={() => rememberActiveRoute(item.route)}
              muted
            />
          ))}
        </SidebarSection>

        <NewNavigationsButtons uiTexts={uiTexts} />
      </div>
    </>
  );
};
